import { useState, useEffect } from 'react';
import axios from 'axios';
import { UserPlus, Trash2, Eye, EyeOff } from 'lucide-react';

const API = (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
  ? 'http://localhost:8001/api'
  : '/api';

function ManagerManagement() {
  const [managers, setManagers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [newManager, setNewManager] = useState({
    username: '',
    password: '',
    full_name: '',
    email: ''
  });
  
  // Modal states
  const [alertModal, setAlertModal] = useState({ show: false, message: '', type: 'info' });
  const [confirmModal, setConfirmModal] = useState({ show: false, message: '', onConfirm: null });
  
  const showAlert = (message, type = 'info') => setAlertModal({ show: true, message, type });
  const showConfirm = (message, onConfirm) => setConfirmModal({ show: true, message, onConfirm });

  const token = localStorage.getItem('token');
  const axiosConfig = {
    headers: { Authorization: `Bearer ${token}` }
  };

  useEffect(() => {
    fetchManagers();
  }, []);

  const fetchManagers = async () => {
    try {
      const response = await axios.get(`${API}/admin/managers`, axiosConfig);
      setManagers(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching managers:', error);
      setLoading(false);
    }
  };

  const handleCreateManager = async () => {
    try {
      await axios.post(`${API}/admin/managers`, newManager, axiosConfig);
      showAlert('✅ Yönetici oluşturuldu!', 'success');
      setShowCreateModal(false);
      setNewManager({ username: '', password: '', full_name: '', email: '' });
      fetchManagers();
    } catch (error) {
      showAlert('❌ ' + (error.response?.data?.detail || 'Hata oluştu!'), 'error');
    }
  };

  const handleDeleteManager = async (managerId) => {
    showConfirm('Bu yöneticiyi silmek istediğinizden emin misiniz?', async () => {
      try {
        await axios.delete(`${API}/admin/managers/${managerId}`, axiosConfig);
        showAlert('✅ Yönetici silindi!', 'success');
        fetchManagers();
      } catch (error) {
        showAlert('❌ ' + (error.response?.data?.detail || 'Hata oluştu!'), 'error');
      }
    });
  };

  if (loading) {
    return <div className="text-white text-center py-12">Yükleniyor...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-white font-bold text-2xl mb-2">Yönetici Hesapları</h2>
          <p className="text-gray-400">Manager hesaplarını buradan oluşturun ve yönetin</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg flex items-center gap-2 transition-colors"
        >
          <UserPlus className="w-5 h-5" />
          Yeni Yönetici Ekle
        </button>
      </div>

      <div className="bg-blue-900/20 border border-blue-500/30 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <div className="text-blue-400 text-2xl">💡</div>
          <div className="text-blue-300 text-sm">
            <strong>Yönetici Nedir?</strong>
            <p className="mt-2 text-gray-300">
              Yöneticiler, kullanıcıları gruplara ekleyebilir ve her grup için özel fiyat marjları belirleyebilir.
              Normal giriş sayfasından email ve şifre ile sisteme girebilir.
            </p>
          </div>
        </div>
      </div>

      {managers.length === 0 ? (
        <div className="bg-gray-800 rounded-xl p-12 text-center border border-gray-700">
          <p className="text-gray-400 text-lg">Henüz yönetici hesabı oluşturmadınız</p>
        </div>
      ) : (
        <div className="bg-gray-800 rounded-xl border border-gray-700">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-700">
                  <th className="text-left p-4 text-gray-400 font-medium">Kullanıcı Adı</th>
                  <th className="text-left p-4 text-gray-400 font-medium">Ad Soyad</th>
                  <th className="text-left p-4 text-gray-400 font-medium">Email</th>
                  <th className="text-left p-4 text-gray-400 font-medium">Oluşturulma</th>
                  <th className="text-right p-4 text-gray-400 font-medium">İşlemler</th>
                </tr>
              </thead>
              <tbody>
                {managers.map(manager => (
                  <tr key={manager.id} className="border-b border-gray-700/50 hover:bg-gray-700/30">
                    <td className="p-4">
                      <span className="text-white font-medium">{manager.username}</span>
                    </td>
                    <td className="p-4 text-gray-300">{manager.full_name}</td>
                    <td className="p-4 text-gray-400">{manager.email}</td>
                    <td className="p-4 text-gray-400">
                      {new Date(manager.created_at).toLocaleDateString('tr-TR')}
                    </td>
                    <td className="p-4 text-right">
                      <button
                        onClick={() => handleDeleteManager(manager.id)}
                        className="text-red-400 hover:text-red-300 transition-colors"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-gray-800 rounded-xl p-8 w-full max-w-md border border-gray-700">
            <h2 className="text-white font-bold text-2xl mb-6">Yeni Yönetici Oluştur</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-gray-300 mb-2">Kullanıcı Adı</label>
                <input
                  type="text"
                  value={newManager.username}
                  onChange={(e) => setNewManager({ ...newManager, username: e.target.value })}
                  className="w-full bg-gray-700 text-white px-4 py-3 rounded-lg outline-none"
                  placeholder="manager1"
                />
              </div>
              
              <div>
                <label className="block text-gray-300 mb-2">Ad Soyad</label>
                <input
                  type="text"
                  value={newManager.full_name}
                  onChange={(e) => setNewManager({ ...newManager, full_name: e.target.value })}
                  className="w-full bg-gray-700 text-white px-4 py-3 rounded-lg outline-none"
                  placeholder="Ahmet Yılmaz"
                />
              </div>
              
              <div>
                <label className="block text-gray-300 mb-2">Email</label>
                <input
                  type="email"
                  value={newManager.email}
                  onChange={(e) => setNewManager({ ...newManager, email: e.target.value })}
                  className="w-full bg-gray-700 text-white px-4 py-3 rounded-lg outline-none"
                  placeholder="manager@trading.com"
                />
              </div>
              
              <div>
                <label className="block text-gray-300 mb-2">Şifre</label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={newManager.password}
                    onChange={(e) => setNewManager({ ...newManager, password: e.target.value })}
                    className="w-full bg-gray-700 text-white px-4 py-3 rounded-lg outline-none pr-12"
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowCreateModal(false)}
                className="flex-1 bg-gray-700 hover:bg-gray-600 text-white py-3 rounded-lg transition-colors"
              >
                İptal
              </button>
              <button
                onClick={handleCreateManager}
                disabled={!newManager.username || !newManager.password || !newManager.full_name || !newManager.email}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-lg transition-colors disabled:opacity-50"
              >
                Oluştur
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Site İçi Alert Modal */}
      {alertModal.show && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70">
          <div className="bg-gray-800 rounded-xl w-full max-w-sm p-5 shadow-2xl">
            <div className="text-center">
              <div className="text-4xl mb-3">
                {alertModal.type === 'success' ? '✅' : alertModal.type === 'error' ? '❌' : 'ℹ️'}
              </div>
              <p className={`text-sm mb-4 whitespace-pre-line ${alertModal.type === 'success' ? 'text-green-400' : alertModal.type === 'error' ? 'text-red-400' : 'text-gray-300'}`}>
                {alertModal.message}
              </p>
              <button
                onClick={() => setAlertModal({ show: false, message: '', type: 'info' })}
                className="w-full py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
              >
                Tamam
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Site İçi Confirm Modal */}
      {confirmModal.show && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70">
          <div className="bg-gray-800 rounded-xl w-full max-w-sm p-5 shadow-2xl">
            <div className="text-center">
              <div className="text-4xl mb-3">⚠️</div>
              <p className="text-sm mb-4 text-gray-300">{confirmModal.message}</p>
              <div className="flex gap-3">
                <button
                  onClick={() => setConfirmModal({ show: false, message: '', onConfirm: null })}
                  className="flex-1 py-2.5 bg-gray-600 hover:bg-gray-700 text-white rounded-lg font-medium transition-colors"
                >
                  İptal
                </button>
                <button
                  onClick={() => {
                    if (confirmModal.onConfirm) confirmModal.onConfirm();
                    setConfirmModal({ show: false, message: '', onConfirm: null });
                  }}
                  className="flex-1 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors"
                >
                  Evet
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ManagerManagement;
